import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';

/**
 * Left sidebar — main app navigation.
 *
 * Two width modes (OCP-54):
 *   - expanded  (default): `w-56`, shows icon + label
 *   - collapsed:           `w-14`, icon-only with native browser tooltip
 *     (label rendered as `title` attribute on the NavLink)
 *
 * State persists in localStorage so it survives reloads. Toggle
 * button sits at the top of the sidebar (chevron pointing in the
 * direction the sidebar would move on click).
 *
 * Active state stays brand-color in both modes — that's the visual
 * anchor an operator needs to know which page they're on at a glance.
 */
const LOCAL_STORAGE_KEY = 'orboto-admin-sidebar-collapsed';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
}

interface NavSection {
  label: string;
  items: NavItem[];
}

// Heroicons-outline-style, stroke-width 1.5, viewBox 24×24.
// Inlined to avoid an icon-lib dependency for 5 nav items.
const ICONS = {
  dashboard: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3.75" y="3.75" width="6.5" height="6.5" rx="1" />
      <rect x="13.75" y="3.75" width="6.5" height="6.5" rx="1" />
      <rect x="3.75" y="13.75" width="6.5" height="6.5" rx="1" />
      <rect x="13.75" y="13.75" width="6.5" height="6.5" rx="1" />
    </svg>
  ),
  tenants: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path d="M3.75 21V6.75A1.5 1.5 0 0 1 5.25 5.25h5A1.5 1.5 0 0 1 11.75 6.75V21M3.75 21h8" />
      <path d="M11.75 21v-9A1.5 1.5 0 0 1 13.25 10.5h5A1.5 1.5 0 0 1 19.75 12V21M11.75 21h8M11.75 21h8" />
      <path d="M6.5 9.25h1.5M6.5 12.25h1.5M6.5 15.25h1.5M14.75 14.25h2M14.75 17.25h2" strokeLinecap="round" />
    </svg>
  ),
  codes: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        d="M2.75 9.5V7a1.5 1.5 0 0 1 1.5-1.5h15.5A1.5 1.5 0 0 1 21.25 7v2.5a2 2 0 1 0 0 5V17a1.5 1.5 0 0 1-1.5 1.5H4.25A1.5 1.5 0 0 1 2.75 17v-2.5a2 2 0 1 0 0-5Z"
        strokeLinejoin="round"
      />
      <path d="M9.5 8v8" strokeLinecap="round" strokeDasharray="1.5 2.5" />
    </svg>
  ),
  waitlist: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="5.25" y="3.75" width="13.5" height="16.5" rx="1.5" />
      <path d="M8.75 8h6.5M8.75 12h6.5M8.75 16h4" strokeLinecap="round" />
    </svg>
  ),
  servers: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3.75" y="4.5" width="16.5" height="6" rx="1" />
      <rect x="3.75" y="13.5" width="16.5" height="6" rx="1" />
      <circle cx="6.75" cy="7.5" r="0.75" fill="currentColor" stroke="none" />
      <circle cx="6.75" cy="16.5" r="0.75" fill="currentColor" stroke="none" />
      <path d="M10.5 7.5h6M10.5 16.5h6" strokeLinecap="round" />
    </svg>
  ),
  jobs: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        d="M20 12a8 8 0 1 1-2.34-5.66M20 4v4.5h-4.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  audit: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        d="M9.25 3.75A1.5 1.5 0 0 0 7.75 5.25v0h-2A1.5 1.5 0 0 0 4.25 6.75v12.5A1.5 1.5 0 0 0 5.75 20.75h12.5A1.5 1.5 0 0 0 19.75 19.25V6.75A1.5 1.5 0 0 0 18.25 5.25h-2v0a1.5 1.5 0 0 0-1.5-1.5h-5.5Z"
        strokeLinejoin="round"
      />
      <path d="M8.75 12.5l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  settings: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="12" cy="12" r="3" />
      <path
        d="M19.4 15a1.7 1.7 0 0 0 .34 1.87l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.7 1.7 0 0 0-1.87-.34 1.7 1.7 0 0 0-1.03 1.56V21a2 2 0 1 1-4 0v-.08a1.7 1.7 0 0 0-1.11-1.56 1.7 1.7 0 0 0-1.87.34l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.7 1.7 0 0 0 4.6 15a1.7 1.7 0 0 0-1.56-1.03H3a2 2 0 1 1 0-4h.08A1.7 1.7 0 0 0 4.6 8.94a1.7 1.7 0 0 0-.34-1.87l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.7 1.7 0 0 0 1.87.34H9a1.7 1.7 0 0 0 1.03-1.56V3a2 2 0 1 1 4 0v.08A1.7 1.7 0 0 0 15.06 4.6a1.7 1.7 0 0 0 1.87-.34l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.7 1.7 0 0 0-.34 1.87V9a1.7 1.7 0 0 0 1.56 1.03H21a2 2 0 1 1 0 4h-.08A1.7 1.7 0 0 0 19.4 15Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  encryption: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="5" y="11" width="14" height="9" rx="1.5" />
      <path d="M8 11V7a4 4 0 1 1 8 0v4" />
      <circle cx="12" cy="15.5" r="1.25" fill="currentColor" stroke="none" />
    </svg>
  ),
  mail: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="3.5" y="5.5" width="17" height="13" rx="1.5" />
      <path d="m4 7 8 6 8-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  maintenance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
      <path
        d="M14.7 6.3a3.5 3.5 0 0 0-4.6 4.5L4 16.9V20h3.1l6.1-6.1a3.5 3.5 0 0 0 4.5-4.6l-2.3 2.3-2-2 2.3-2.3Z"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  ),
  chevronLeft: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="m14.5 6.5-5.5 5.5 5.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  chevronRight: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75">
      <path d="m9.5 6.5 5.5 5.5-5.5 5.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

// OCP-164 — sections group the previously-flat list of 20+ nav items
// so the sidebar stays scannable as more pages land. Section headers
// render as small uppercase labels in expanded mode; in collapsed mode
// only a thin divider between sections remains.
const sections: NavSection[] = [
  {
    label: 'Overview',
    items: [{ to: '/', label: 'Dashboard', icon: ICONS.dashboard }],
  },
  {
    label: 'Customers',
    items: [
      { to: '/accounts', label: 'Accounts', icon: ICONS.tenants },
      { to: '/tenants', label: 'Tenants', icon: ICONS.tenants },
      { to: '/tenants/health', label: 'Tenant health', icon: ICONS.tenants },
      { to: '/lifecycle/hard-delete', label: 'Hard-delete queue', icon: ICONS.tenants },
      // OCP-846 - AGB V4 6.5 payment defaults and their termination dates.
      { to: '/payment-defaults', label: 'Payment defaults', icon: ICONS.tenants },
      { to: '/portal-codes', label: 'Portal codes', icon: ICONS.codes },
      { to: '/waitlist', label: 'Waitlist', icon: ICONS.waitlist },
      { to: '/selfhost-notify', label: 'Self-host notify', icon: ICONS.waitlist },
      { to: '/codes', label: 'Invite codes', icon: ICONS.codes },
      // OCP-911 / OCP-917 - code-granted workspaces running out (reminders, grace, shutdown).
      { to: '/code-grants', label: 'Code grants', icon: ICONS.codes },
      // OCP-991 - single-use discount codes, the daily cap, issue and revoke.
      { to: '/discount-codes', label: 'Discount codes', icon: ICONS.codes },
      // OCP-1036 - prices agreed with one named account (no automatic volume discount).
      { to: '/company-packages', label: 'Company packages', icon: ICONS.codes },
      { to: '/redeem-attempts', label: 'Redeem attempts', icon: ICONS.audit },
      { to: '/feedback', label: 'Feedback intake', icon: ICONS.mail },
      // OCP-617 - support@orboto.io and its aliases as SUP tickets, replies, SLA.
      { to: '/support-inbox', label: 'Support inbox', icon: ICONS.mail },
      // OCP-557 - escalated tenant errors, the only cross-tenant error visibility.
      { to: '/errors', label: 'Tenant errors', icon: ICONS.audit },
      { to: '/provisioning-approvals', label: 'Provisioning approvals', icon: ICONS.codes },
      { to: '/licenses', label: 'Self-host licenses', icon: ICONS.encryption },
      { to: '/drip/templates', label: 'Drip templates', icon: ICONS.codes },
    ],
  },
  {
    label: 'Mail',
    items: [
      { to: '/oms/customers', label: 'Mail customers', icon: ICONS.mail },
      { to: '/oms/sends', label: 'Mail sends', icon: ICONS.mail },
      { to: '/oms/suppression', label: 'Mail suppression', icon: ICONS.mail },
      { to: '/oms/quotas', label: 'Mail quotas', icon: ICONS.mail },
      { to: '/oms/templates', label: 'Mail templates', icon: ICONS.mail },
      { to: '/oms/system-status', label: 'Mail status', icon: ICONS.audit },
      // OCP-206 — operator-internal sends (OCP's own transactional mail).
      // Separate from customer-facing /oms/sends so the operator can see
      // their own outbound stream without filtering customer-id manually.
      { to: '/mail/operator/sends', label: 'Operator mail', icon: ICONS.mail },
      { to: '/mail/operator/suppressions', label: 'Operator suppressions', icon: ICONS.mail },
      // OCP-633 - announcements to selected customer contacts (preview then send).
      { to: '/mail/announcements', label: 'Announcements', icon: ICONS.mail },
    ],
  },
  {
    label: 'Billing',
    items: [
      { to: '/products', label: 'Products', icon: ICONS.codes },
      { to: '/billing-providers', label: 'Billing import', icon: ICONS.codes },
      { to: '/referrals', label: 'Referrals & affiliates', icon: ICONS.codes },
      { to: '/affiliate-payouts', label: 'Affiliate payouts', icon: ICONS.codes },
      { to: '/terms', label: 'Terms', icon: ICONS.audit },
      // OCP-608 - costs against revenue per month.
      { to: '/finance', label: 'Finance (P&L)', icon: ICONS.codes },
    ],
  },
  {
    label: 'Operations',
    items: [
      { to: '/team', label: 'Team', icon: ICONS.tenants },
      // OCP-930 - agents ask, a person approves with a fresh second factor.
      { to: '/approvals', label: 'Approvals', icon: ICONS.encryption },
      { to: '/servers', label: 'Servers', icon: ICONS.servers },
      { to: '/server-health', label: 'Server health', icon: ICONS.servers },
      { to: '/maintenance', label: 'Maintenance windows', icon: ICONS.maintenance },
      { to: '/gateway-models', label: 'AI gateway models', icon: ICONS.servers },
      { to: '/training-data', label: 'AI training data', icon: ICONS.audit },
      // OCP-632 - nightly golden-set evaluation of the chat models.
      { to: '/ai/quality', label: 'AI quality', icon: ICONS.audit },
      { to: '/jobs', label: 'Jobs', icon: ICONS.jobs },
      { to: '/oauth-clients', label: 'OAuth clients', icon: ICONS.encryption },
      { to: '/audit', label: 'Audit log', icon: ICONS.audit },
      { to: '/observability', label: 'Observability', icon: ICONS.audit },
      { to: '/notifications', label: 'Notifications', icon: ICONS.audit },
      { to: '/encryption', label: 'Encryption', icon: ICONS.encryption },
      // OCP-755 - the control plane's own DB backup + monthly restore drill.
      { to: '/ops/backups', label: 'Backups & drills', icon: ICONS.jobs },
      // OCP-423 - nightly restic backup of the tenant file storage + restores.
      { to: '/ops/file-backups', label: 'File backups', icon: ICONS.jobs },
      // OCP-755 - quarterly access review.
      { to: '/ops/access-review', label: 'Access review', icon: ICONS.encryption },
      { to: '/settings', label: 'Settings', icon: ICONS.settings },
    ],
  },
];

/** Flat list used by `pickActiveTo` for longest-prefix-match. */
const allItems: NavItem[] = sections.flatMap((s) => s.items);

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(LOCAL_STORAGE_KEY) === '1';
  } catch {
    // SSR / sandboxed env — default to expanded.
    return false;
  }
}

/**
 * Pick the nav item with the longest prefix match for the current
 * path. Why: react-router's NavLink highlights every prefix-matching
 * route, so on `/tenants/health` both "Tenants" (`/tenants`) and
 * "Tenant health" (`/tenants/health`) light up. Slapping `end` on
 * `/tenants` would fix that but break highlighting on
 * `/tenants/:id` detail pages. Longest-prefix-wins keeps both: a
 * deeper nav-item beats its parent, but a parent still wins for
 * paths that have no matching child nav-item.
 */
function pickActiveTo(items: NavItem[], pathname: string): string | null {
  let best: string | null = null;
  let bestLen = -1;
  for (const it of items) {
    if (it.to === '/') {
      if (pathname === '/' && bestLen < 0) {
        best = '/';
        bestLen = 0;
      }
      continue;
    }
    const matches = pathname === it.to || pathname.startsWith(it.to + '/');
    if (matches && it.to.length > bestLen) {
      best = it.to;
      bestLen = it.to.length;
    }
  }
  return best;
}

/** Shared nav body — rendered by both the desktop aside and the mobile drawer. */
function NavSections({
  collapsed,
  activeTo,
  onNavigate,
}: {
  collapsed: boolean;
  activeTo: string | null;
  onNavigate?: () => void;
}) {
  return (
    <>
      {sections.map((section, sectionIdx) => (
        <div key={section.label} className="flex flex-col gap-1">
          {sectionIdx > 0 &&
            (collapsed ? (
              <div className="mx-2 my-1.5 border-t border-gray-200 dark:border-gray-800" />
            ) : (
              <div className="mt-3 px-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {section.label}
              </div>
            ))}
          {sectionIdx === 0 && !collapsed && (
            <div className="px-2.5 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
              {section.label}
            </div>
          )}
          {section.items.map((it) => {
            const isActive = it.to === activeTo;
            return (
              <Link
                key={it.to}
                to={it.to}
                title={collapsed ? it.label : undefined}
                aria-current={isActive ? 'page' : undefined}
                onClick={onNavigate}
                className={`flex items-center gap-2.5 rounded px-2.5 py-1.5 text-sm ${
                  isActive
                    ? 'bg-brand-500 text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800/60'
                } ${collapsed ? 'justify-center' : ''}`}
              >
                <span className="block h-5 w-5 shrink-0">{it.icon}</span>
                {!collapsed && <span className="truncate">{it.label}</span>}
              </Link>
            );
          })}
        </div>
      ))}
    </>
  );
}

export interface SidebarProps {
  /** OCP-333 — mobile slide-in drawer open state (controlled by AppShell). */
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function Sidebar({ mobileOpen = false, onMobileClose }: SidebarProps) {
  const [collapsed, setCollapsed] = useState<boolean>(readCollapsed);
  const location = useLocation();
  const activeTo = useMemo(
    () => pickActiveTo(allItems, location.pathname),
    [location.pathname],
  );

  // Re-read on mount in case the initial render happened in a context
  // where localStorage was unavailable.
  useEffect(() => {
    setCollapsed(readCollapsed());
  }, []);

  // OCP-333 — close the mobile drawer on Escape.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onMobileClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen, onMobileClose]);

  const toggle = useCallback(() => {
    setCollapsed((c) => {
      const next = !c;
      try {
        localStorage.setItem(LOCAL_STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  return (
    <>
      {/* Desktop: static collapsible aside (md+). */}
      <aside
        className={`hidden shrink-0 flex-col border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 md:flex ${
          collapsed ? 'w-14' : 'w-56'
        }`}
      >
        <div className={`flex ${collapsed ? 'justify-center' : 'justify-end'} px-2 pt-3`}>
          <button
            type="button"
            onClick={toggle}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className="flex h-7 w-7 items-center justify-center rounded text-gray-500 hover:bg-gray-200 dark:hover:bg-gray-700/70 hover:text-gray-800 dark:hover:text-gray-200"
          >
            <span className="block h-4 w-4">
              {collapsed ? ICONS.chevronRight : ICONS.chevronLeft}
            </span>
          </button>
        </div>
        <nav className="mt-2 flex flex-1 flex-col gap-1 overflow-y-auto px-2">
          <NavSections collapsed={collapsed} activeTo={activeTo} />
        </nav>
        {/* OCP-334 — version pinned to the bottom of the nav column. */}
        <VersionFooter collapsed={collapsed} />
      </aside>

      {/* Mobile: slide-in drawer overlay (<md), controlled by AppShell. */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-black/40"
            aria-hidden="true"
            onClick={onMobileClose}
          />
          <aside className="absolute left-0 top-0 flex h-full w-64 max-w-[85%] flex-col border-r border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-900 shadow-xl">
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-gray-200 dark:border-gray-800 px-4">
              <span className="text-sm font-medium tracking-tight text-gray-500">Menu</span>
              <button
                type="button"
                onClick={onMobileClose}
                aria-label="Close menu"
                className="flex h-8 w-8 items-center justify-center rounded text-gray-500 hover:bg-gray-200/70 hover:text-gray-800 dark:hover:text-gray-100"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.75">
                  <path d="m6 6 12 12M18 6 6 18" strokeLinecap="round" />
                </svg>
              </button>
            </div>
            <nav className="mt-2 flex flex-1 flex-col gap-1 overflow-y-auto px-2 pb-4">
              <NavSections collapsed={false} activeTo={activeTo} onNavigate={onMobileClose} />
            </nav>
            <VersionFooter collapsed={false} />
          </aside>
        </div>
      )}
    </>
  );
}

/** OCP-334 — app version pinned to the bottom of the nav column. */
function VersionFooter({ collapsed }: { collapsed: boolean }) {
  return (
    <div
      className={`shrink-0 border-t border-gray-200 dark:border-gray-800 px-2 py-2 font-mono text-xs text-gray-400 ${
        collapsed ? 'text-center' : 'px-3'
      }`}
      title="App version (deployed build)"
      data-testid="app-version-badge"
    >
      {collapsed ? __APP_VERSION__.split('+')[0] : `v${__APP_VERSION__}`}
    </div>
  );
}
